import { useBehaviourState } from '@sravni/react-silent-authentication-form';
import { usePrevious } from '@sravni/react-utils';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { isSatisfyOption } from 'shared/lib';

import { SCREENS, FIELD_NAME } from '../constants';
import type { AuthInlineFormProps } from '../types';

import { AuthInlineFormDesktopScreen } from './AuthInlineFormDesktopScreen';
import { AuthInlineFormFields } from './AuthInlineFormFields';
import { AuthInlineFormMobileScreen } from './AuthInlineFormMobileScreen';

export const AuthInlineFormView = (props: AuthInlineFormProps) => {
  const {
    isEnabledNameField = false,
    isEnabledEmailField = true,
    advertisingBanner,
    getOTPButtonProps,
    consentsScreenTitle,
    advertisingCheckboxLabel,
    onScreenChange,
    onChangeAgreementAdvertising,
    onChangeAgreementPersonalData,
    children,
    onChangePhoneClick,
    onClose,
    onConsentsScreenOpen,
    onOTPScreenOpen,
    onConsentsSubmitClick,
    isOTPButtonSticky = false,
    isEnabledOTPButtonSticky = true,
    onGetNewOTPClick,
    onInvalidSmsCodeError,
  } = props;

  const { setValue, watch, formState } = useFormContext();
  const { errors } = formState;
  const { isConsentsReceived } = useBehaviourState();
  const [currentScreen, setCurrentScreen] = useState<ValuesOf<typeof SCREENS>>(SCREENS.INITIAL);
  const prevScreen = usePrevious(currentScreen);
  const isDialogVisible = isSatisfyOption([SCREENS.OTP, SCREENS.CONSENTS], currentScreen);
  const [submitAttempt, setSubmitAttempt] = useState(0);
  const [agreementPersonalData, agreementAdvertising] = watch([
    FIELD_NAME.AGREEMENT_PERSONAL_DATA,
    FIELD_NAME.AGREEMENT_ADVERTISING,
  ]);

  const otpErrorMessage = errors[FIELD_NAME.OTP]?.message;

  const onDialogClose = useCallback(() => {
    onClose?.();
    setValue(FIELD_NAME.OTP, undefined);
    setCurrentScreen(SCREENS.INITIAL);
  }, [setValue, onClose]);

  const handleChangePhoneClick = useCallback(() => {
    onChangePhoneClick?.();
    setValue(FIELD_NAME.OTP, undefined);
    setCurrentScreen(SCREENS.INITIAL);
  }, [setValue, onChangePhoneClick]);

  const onReturnButtonClick = useCallback(() => {
    setValue(FIELD_NAME.OTP, undefined);
    setCurrentScreen(SCREENS.OTP);
  }, [setValue]);

  useEffect(() => {
    if (otpErrorMessage === 'Неверный код') {
      onInvalidSmsCodeError?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpErrorMessage]);

  useEffect(() => {
    if (isConsentsReceived === false) {
      setCurrentScreen(SCREENS.CONSENTS);
    }
  }, [isConsentsReceived]);

  useEffect(() => {
    if (typeof prevScreen !== 'undefined' && prevScreen !== currentScreen) onScreenChange?.(currentScreen);
  }, [currentScreen, onScreenChange, prevScreen]);

  useEffect(() => {
    setSubmitAttempt(0);
  }, [agreementAdvertising, agreementPersonalData]);

  const onConsentsSubmitButtonClick: React.MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (!agreementAdvertising && submitAttempt === 0) {
        event.preventDefault();
        event.stopPropagation();
        setSubmitAttempt((prev) => prev + 1);
        onConsentsSubmitClick?.({
          isAgreementPersonalData: agreementPersonalData,
          isAgreementAdvertising: agreementAdvertising,
          isSubmitted: false,
        });
      } else {
        onConsentsSubmitClick?.({
          isAgreementPersonalData: agreementPersonalData,
          isAgreementAdvertising: agreementAdvertising,
          isSubmitted: true,
        });
      }
    },
    [agreementAdvertising, submitAttempt, setSubmitAttempt, onConsentsSubmitClick, agreementPersonalData],
  );

  return (
    <>
      <AuthInlineFormFields
        isEnabledNameField={isEnabledNameField}
        isEnabledEmailField={isEnabledEmailField}
        getOTPButtonProps={getOTPButtonProps}
        currentScreen={currentScreen}
        setCurrentScreen={setCurrentScreen}
        isOTPButtonSticky={isOTPButtonSticky}
        isEnabledOTPButtonSticky={isEnabledOTPButtonSticky}
      >
        {children}
      </AuthInlineFormFields>

      <AuthInlineFormMobileScreen
        currentScreen={currentScreen}
        submitAttempt={submitAttempt}
        onConsentsSubmitClick={onConsentsSubmitButtonClick}
        onClose={onDialogClose}
        onChangePhoneClick={handleChangePhoneClick}
        consentsScreenTitle={consentsScreenTitle}
        onOTPScreenOpen={onOTPScreenOpen}
        advertisingCheckboxLabel={advertisingCheckboxLabel}
        onChangeAgreementAdvertising={onChangeAgreementAdvertising}
        onChangeAgreementPersonalData={onChangeAgreementPersonalData}
        onConsentsScreenOpen={onConsentsScreenOpen}
        isDialogVisible={isDialogVisible}
        onReturnButtonClick={onReturnButtonClick}
        onGetNewOTPClick={onGetNewOTPClick}
      />

      <AuthInlineFormDesktopScreen
        currentScreen={currentScreen}
        submitAttempt={submitAttempt}
        onConsentsSubmitClick={onConsentsSubmitButtonClick}
        onClose={onDialogClose}
        onChangePhoneClick={handleChangePhoneClick}
        consentsScreenTitle={consentsScreenTitle}
        advertisingBanner={advertisingBanner}
        onOTPScreenOpen={onOTPScreenOpen}
        advertisingCheckboxLabel={advertisingCheckboxLabel}
        onChangeAgreementAdvertising={onChangeAgreementAdvertising}
        onChangeAgreementPersonalData={onChangeAgreementPersonalData}
        onConsentsScreenOpen={onConsentsScreenOpen}
        isDialogVisible={isDialogVisible}
        onReturnButtonClick={onReturnButtonClick}
        onGetNewOTPClick={onGetNewOTPClick}
      />
    </>
  );
};
